import { useEffect, useRef, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { isUserIdle } from "../lib/userIdle";

const POLL_INTERVAL = 60_000;
// iter r12 — always-open tabs (kanban wall display, overnight laptops)
// never change route, so they ran stale builds for WEEKS after deploys
// (5 different build hashes seen in one day of prod error stacks — old
// builds re-introduced already-fixed error floods). If an update is
// pending and the user hasn't touched the tab for 10 min (or it's
// hidden), reload — safe, and the page comes back exactly where it was.
const IDLE_RELOAD_MS = 10 * 60 * 1000;

// Simple pub/sub so the banner re-renders the moment polling flips state.
let knownVersion = null;
let updateAvailable = false;
const listeners = new Set();
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const getSnapshot = () => updateAvailable;
const setUpdateAvailable = (v) => {
  if (updateAvailable === v) return;
  updateAvailable = v;
  listeners.forEach((l) => l());
};

/**
 * Returns true once a newer build is detected on the server.
 * Drives the top banner. Reset by reloading the page.
 */
export function useUpdateAvailable() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Polls /version.json every 60s. When a new version lands:
 *   - updateAvailable flips → top banner appears
 *   - On the user's NEXT route change, auto-reloads so chunks line up
 *   - Never interrupts active work (forms / dialogs)
 */
export function useVersionCheck() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  // On route change: if update is pending, reload to get fresh chunks
  useEffect(() => {
    if (prevPath.current !== location.pathname && updateAvailable) {
      window.location.reload();
    }
    prevPath.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    async function check() {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (!knownVersion) {
          knownVersion = data.version;
        } else if (data.version !== knownVersion) {
          console.log(`[Version] New deploy detected: ${knownVersion} -> ${data.version}`);
          setUpdateAvailable(true);
        }
      } catch {}
    }
    check();
    const id = setInterval(() => {
      // Hidden tabs still check (browser throttles the timer to ~1/min
      // anyway) — otherwise an overnight background tab never learns a
      // deploy happened and wakes up running weeks-old code.
      check();
      if (updateAvailable && (document.hidden || isUserIdle(IDLE_RELOAD_MS))) {
        window.location.reload();
      }
    }, POLL_INTERVAL);
    return () => clearInterval(id);
  }, []);
}

// Re-export for tests if ever needed
export { setUpdateAvailable as __setUpdateAvailableForTests };
